import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import SEO from "@/components/SEO";

export default function Campaigns() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/campaigns")
      .then((r) => setItems(r.data || []))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="campaigns-page" className="bg-[#F9F9F4] text-[#1A3626]">
      <SEO
        title="Campaigns"
        path="/campaigns"
        description="The Wet Cabbage campaign archive. Tipple Tours, motel chaos, retail park editorials and other cinematic dispatches from the Department of Modern Leisure."
      />

      <section className="bg-[#F9F9F4]" data-testid="campaigns-masthead">
        <div className="mx-auto max-w-[1400px] px-6 pb-12 pt-20 md:px-10 md:pb-20 md:pt-32">
          <div className="font-display text-[10px] font-black uppercase tracking-[0.42em] text-[#93C065] md:text-xs">
            Campaigns &mdash; Archive
          </div>
          <h1 className="font-display mt-5 text-5xl font-black uppercase leading-[0.9] tracking-tight sm:text-7xl md:text-8xl lg:text-[120px]">
            The Lookbook.
          </h1>
          <p className="mt-8 max-w-2xl text-base leading-relaxed text-[#1A3626] md:text-xl md:leading-[1.6]">
            Cinematic dispatches from inside the universe. Tipple tours, motel
            chaos, retail park editorials and other moments of dignified average
            behaviour.
          </p>
        </div>
      </section>

      <section className="bg-[#F9F9F4]" data-testid="campaigns-grid">
        <div className="mx-auto max-w-[1400px] px-6 pb-24 pt-4 md:px-10 md:pb-32 md:pt-12">
          {loading ? (
            <div className="grid grid-cols-1 gap-12 md:grid-cols-2">
              {Array.from({ length: 4 }).map((_, i) => (
                <div
                  key={i}
                  className="aspect-[4/5] animate-pulse bg-[#EBE7DD]"
                />
              ))}
            </div>
          ) : items.length === 0 ? (
            <div className="border-2 border-dashed border-[#1A3626] p-20 text-center">
              <div className="font-display text-[10px] font-bold uppercase tracking-[0.4em] text-[#1A3626]/60 md:text-xs">
                Currently in production
              </div>
              <p className="mx-auto mt-4 max-w-md text-base leading-relaxed text-[#1A3626]/80 md:text-lg">
                The Wet Cabbage campaign archive is currently empty &mdash;
                shoots are being supervised slowly and seriously by one curator
                and a dog named Mush. New dispatches arrive soon.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-x-10 gap-y-16 md:grid-cols-2 md:gap-y-24">
              {items.map((c, i) => (
                <Link
                  key={c.id}
                  to={`/campaigns/${c.slug}`}
                  className="group block"
                  data-testid={`campaign-card-${c.slug}`}
                >
                  <div className="aspect-[4/5] overflow-hidden bg-[#EBE7DD]">
                    {c.hero_image_url ? (
                      <img
                        src={fileUrl(c.hero_image_url)}
                        alt={c.hero_image_alt || c.title}
                        className="h-full w-full object-cover transition-transform duration-1000 group-hover:scale-[1.03]"
                        loading={i < 2 ? "eager" : "lazy"}
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center font-display text-xs uppercase tracking-[0.3em] text-[#1A3626]/40">
                        {c.title}
                      </div>
                    )}
                  </div>
                  <div className="mt-5 flex flex-wrap items-center gap-3 text-[10px] text-[#1A3626]/70 md:text-xs">
                    {c.season && (
                      <span className="font-display font-black uppercase tracking-[0.32em] text-[#93C065]">
                        {c.season}
                      </span>
                    )}
                    {c.location && (
                      <>
                        <span aria-hidden>&middot;</span>
                        <span className="font-display font-bold uppercase tracking-[0.28em]">
                          {c.location}
                        </span>
                      </>
                    )}
                  </div>
                  <h2 className="font-display mt-3 text-3xl font-black uppercase leading-[0.95] tracking-tight text-[#1A3626] md:text-4xl lg:text-5xl">
                    {c.title}
                  </h2>
                  {c.subtitle && (
                    <p className="mt-3 max-w-md text-base leading-relaxed text-[#1A3626]/80 md:text-lg">
                      {c.subtitle}
                    </p>
                  )}
                  <div className="font-display mt-5 inline-flex items-center gap-2 text-[11px] font-black uppercase tracking-[0.36em] text-[#1A3626] transition-all group-hover:gap-3 md:text-xs">
                    View the campaign
                    <ArrowRight className="h-4 w-4" strokeWidth={2.5} />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
